import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { tap, catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/env';
import { User } from '../models/user.model';
import { UserAuth } from '../models/user-login.model';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';
import { clearThemeCache } from '../../modules/private/configuracoes/identidade-visual/services/theme-repository.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { PainelPermissionsPath, PermissionsDetails } from '../enum/painelPermissionsPath.enum';

const API_URL = `${environment.api_url}/management/auth/login`;
const API_TOKEN = `/management/auth/refresh`;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiService = inject(ApiService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly currentUserSignal = signal<User | null>(null);

  public readonly currentUser: Signal<User | null> = this.currentUserSignal.asReadonly();
  public readonly isAuthenticated = computed(() => !!this.currentUserSignal());

  /**
   * Get decoded token data from storage.
   */
  public getTokenData(): Record<string, unknown> | null {
    const token = this.localStorageService.getToken();
    return this.localStorageService.getDecodedToken(token);
  }

  /**
   * Populate user from token if available.
   */
  public populate(): Observable<unknown> {
    if (this.currentUserSignal()?.id != null) {
      return of(null);
    }
    if (this.localStorageService.getToken()) {
      return this.apiService.post<{ data: { user: User; token: string } }>(API_TOKEN).pipe(
        tap((data) => {
          this.setAuth(data?.data.user, data?.data.token);
        }),
        catchError(() => {
          this.purgeAuth();
          return of(null);
        })
      );
    } else {
      this.purgeAuth();
      return of(null);
    }
  }

  /**
   * Set authentication state and store token.
   */
  private setAuth(user: User, token: string | null, rememberMe: boolean = false): void {
    if (user.role_id === 1) {
      return;
    }
    if (!token) {
      console.error('Token de autenticação não fornecido');
      return;
    }
    // Store the token
    if (rememberMe) {
      this.localStorageService.saveToken(token);
    } else {
      sessionStorage.setItem('jwtToken', token);
    }
    this.currentUserSignal.set(user);
  }

  /**
   * Remove authentication state and redirect to login.
   */
  public async purgeAuth(): Promise<boolean> {
    // Captura o token ANTES de destruí-lo: o POST de logout ainda precisa dele no
    // Authorization para o backend invalidar a sessão.
    const token = this.localStorageService.getToken();

    // Limpa o estado local ANTES do POST. Se a limpeza ficasse para depois e o POST
    // respondesse 401, o interceptor navegaria para /login com o token ainda no
    // storage — o construtor do Login acha o token, faz refresh e reloga o usuário
    // ("flash" da tela de login seguido de login automático).
    // Incondicional (mesmo com o POST de logout falhando): o cache de tema é global ao
    // navegador — deixá-lo vivo vazaria a cor deste tenant para a próxima sessão.
    clearThemeCache();
    this.localStorageService.destroyToken();
    this.currentUserSignal.set(null);

    try {
      await this.http
        .post(
          `${environment.api_url}/management/auth/logout`,
          {},
          {
            headers: {
              // O storage já foi limpo, então o httpTokenInterceptor não anexaria nada.
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'X-Skip-401-Redirect': 'true',
            },
          }
        )
        .toPromise();
    } catch (err) {}
    return this.router.navigate(['/']);
  }

  /**
   * Attempt authentication with user credentials.
   */
  public attemptAuth(
    userInfo: UserAuth,
    rememberMe: boolean = false
  ): Observable<{ data: { user: User; token: string } }> {
    const body = {
      email: userInfo.email,
      password: userInfo.password,
    };
    return this.http.post<{ data: { user: User; token: string } }>(API_URL, body).pipe(
      tap((data) => {
        // Mesma checagem que setAuth faz internamente — replicada aqui de propósito:
        // populate() também chama setAuth() e NÃO deve limpar o cache de tema (é a
        // mesma sessão sendo restaurada, não uma troca de tenant).
        if (data.data.user.role_id !== 1 && data.data.token) {
          clearThemeCache();
        }
        this.setAuth(data.data.user, data.data.token, rememberMe);
      })
    );
  }

  /**
   * Get user information.
   */
  public getUserInfo(): Observable<User | null> {
    return this.http.get<{ data: User }>('/management/auth/me').pipe(
      tap(() => {}),
      map((response) => response?.data ?? null),
      catchError(() => {
        this.purgeAuth();
        return of(null);
      })
    );
  }

  /**
   * Find permission level for a given path.
   */
  private findPermissionsLevelByEnum(currentPath: PainelPermissionsPath): number {
    const user = this.currentUserSignal();
    if (!user || !user.painel_permissions) {
      return PainelPermissionsPath.UNKNOWN;
    }
    const found = user.painel_permissions?.find(
      (permission: any) => permission.path_route === currentPath
    );
    return found?.permission_level ?? PainelPermissionsPath.UNKNOWN;
  }

  /**
   * Check if user can create on the given path.
   */
  public canCreate(currentPath: PainelPermissionsPath): boolean {
    return this.findPermissionsLevelByEnum(currentPath) >= 3;
  }

  /**
   * Check if user can edit on the given path.
   */
  public canEdit(currentPath: PainelPermissionsPath): boolean {
    return this.findPermissionsLevelByEnum(currentPath) >= 2;
  }

  /**
   * Check if user can see on the given path.
   */
  public canSee(currentPath: PainelPermissionsPath): boolean {
    return this.findPermissionsLevelByEnum(currentPath) >= 1;
  }

  /**
   * Get permission enum by URL.
   */
  public getPermissionEnumByUrl(url: string): PainelPermissionsPath | undefined {
    const cleanUrl = url.split('?')[0].replace(/\/$/, '');
    for (const key in PermissionsDetails) {
      if (Object.prototype.hasOwnProperty.call(PermissionsDetails, key)) {
        const detail = PermissionsDetails[key as unknown as PainelPermissionsPath];
        if (detail && typeof detail.url === 'string' && detail.url === cleanUrl) {
          return Number(key) as PainelPermissionsPath;
        }
      }
    }
    return undefined;
  }

  /**
   * Reautentica o usuário atual contra o próprio endpoint de login. `failureReason` só é
   * definido quando `valid` é `false`: `'invalid_credentials'` para 401 (senha errada, ou
   * ausência de usuário autenticado localmente) e `'request_failed'` para qualquer outro
   * motivo (rede, timeout, erro de servidor) — permite ao chamador não exibir "senha
   * incorreta" quando a causa real foi uma falha de conexão.
   */
  reauthenticate(
    password: string
  ): Observable<{ valid: boolean; failureReason?: 'invalid_credentials' | 'request_failed' }> {
    const email = this.currentUserSignal()?.email;

    if (!email) {
      return of({ valid: false, failureReason: 'invalid_credentials' as const });
    }

    return this.http
      .post<{ data: { user: User; token: string } }>(
        API_URL,
        {
          email,
          password,
        },
        {
          headers: {
            'X-Skip-401-Redirect': 'true',
          },
        }
      )
      .pipe(
        map(() => ({ valid: true })),
        catchError((err: HttpErrorResponse) => {
          const failureReason: 'invalid_credentials' | 'request_failed' =
            err.status === 401 ? 'invalid_credentials' : 'request_failed';
          return of({ valid: false, failureReason });
        })
      );
  }
}
